import type { CallJoin } from '@nexo/types';
import { useSyncExternalStore } from 'react';
import { api, apiBaseUrl } from '../../lib/api';
import { t } from '../../lib/i18n';
import { isDesktop, openExternal } from '../../lib/platform';
import { errorMessage } from '../../lib/forms';

/**
 * WebKitGTK as shipped by Ubuntu/Debian has no WebRTC (RTCPeerConnection), so the Linux desktop
 * app cannot hold calls itself: it opens them in the system browser instead.
 */
export const webrtcSupported = typeof RTCPeerConnection !== 'undefined';

export type StartResult = { ok: true; external: boolean } | { ok: false; error: string };

/** Link to the standalone call page; the credentials travel in the fragment (never sent). */
export function standaloneCallUrl(join: CallJoin, title: string, video: boolean): string {
  const payload = btoa(
    String.fromCharCode(
      ...new TextEncoder().encode(
        JSON.stringify({ u: join.url, t: join.token, n: title, v: video }),
      ),
    ),
  );
  const origin = window.location.origin.startsWith('http') ? window.location.origin : apiBaseUrl;
  return `${origin.replace(/\/$/, '')}/call#${encodeURIComponent(payload)}`;
}

export interface CallState {
  join: CallJoin;
  /** Start with the camera on (video call) or off (voice call). */
  video: boolean;
  expanded: boolean;
}

let state: CallState | null = null;
let joining: string | null = null;
const listeners = new Set<() => void>();
const set = (next: CallState | null) => {
  state = next;
  listeners.forEach((l) => l());
};

/** The single call the user is in (one at a time, it survives navigation between pages). */
export const callStore = {
  /** Joins the call of a conversation (in the system browser when there is no WebRTC). */
  async start(channelId: string, video: boolean, title = t('Llamada')): Promise<StartResult> {
    if (state?.join.channelId === channelId) {
      set({ ...state, expanded: true });
      return { ok: true, external: false };
    }
    if (joining === channelId) return { ok: true, external: false };
    joining = channelId;
    try {
      const join = await api.calls.join(channelId);
      if (!webrtcSupported && isDesktop) {
        await openExternal(standaloneCallUrl(join, title, video));
        return { ok: true, external: true };
      }
      set({ join, video, expanded: true });
      return { ok: true, external: false };
    } catch (error) {
      return { ok: false, error: errorMessage(error) };
    } finally {
      joining = null;
    }
  },
  leave: () => set(null),
  setExpanded: (expanded: boolean) => state && set({ ...state, expanded }),
  current: () => state,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export const useCall = () => useSyncExternalStore(callStore.subscribe, () => state);

/* ---------- Docking slot ---------- */

export interface CallSlot {
  channelId: string;
  element: HTMLElement;
}

let slot: CallSlot | null = null;
const slotListeners = new Set<() => void>();

/**
 * Area of the page where the live call is embedded (a voice channel's view). The call keeps a
 * single mounted room and is only repositioned over the slot, so moving it never reconnects.
 */
export const callSlot = {
  set: (next: CallSlot | null) => {
    slot = next;
    slotListeners.forEach((l) => l());
  },
  subscribe: (listener: () => void) => {
    slotListeners.add(listener);
    return () => slotListeners.delete(listener);
  },
};

export const useCallSlot = () => useSyncExternalStore(callSlot.subscribe, () => slot);
